import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  AchievementHero,
  GraduatesDetail,
  OutcomesDetail,
  RetentionDetail,
} from '@/components/achievement';
import { ScrollReveal } from '@/components/motion';
import { ACHIEVEMENT_SOURCE_URL } from '@/data/experience';

/**
 * StudentAchievementRatesPage (/experience/student-achievement-rates) — the
 * accreditation-mandated (ACBSP) dedicated Student Achievement Rates page. It
 * presents the full institution-reported figures for 2021–2025 — retention,
 * graduate counts and ACBSP learning-outcome trends — where the /experience
 * summary section only teases them.
 *
 * Every figure is verified against the authoritative ACBSP page
 * (ACHIEVEMENT_SOURCE_URL) and read from @/data/experience. The page closes with
 * a provenance link back to the published source. Sections own their own
 * reduced-motion-safe motion.
 */
export function StudentAchievementRatesPage() {
  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <AchievementHero />

      {/* ── RETENTION (MBA · DBA · institution-wide) ─────────────────────── */}
      <RetentionDetail />

      {/* ── GRADUATES (ACBSP MBA/DBA + institution-wide) ─────────────────── */}
      <GraduatesDetail />

      {/* ── ACBSP LEARNING-OUTCOME TRENDS ────────────────────────────────── */}
      <OutcomesDetail />

      {/* ── Provenance + back link ───────────────────────────────────────── */}
      <section className="border-t border-border bg-background py-12">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 sm:flex-row sm:items-center sm:justify-between">
          <ScrollReveal from="up">
            <Link
              to="/experience#achievement-rates"
              data-cursor
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-navy transition-colors duration-300 hover:text-brand-gold motion-reduce:transition-none"
            >
              <ArrowLeft aria-hidden className="size-4" />
              Back to IBAS Experience
            </Link>
          </ScrollReveal>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Figures are institution-reported for 2021–2025, as published on the{' '}
            <a
              href={ACHIEVEMENT_SOURCE_URL}
              target="_blank"
              rel="noreferrer"
              data-cursor
              className="font-medium text-brand-navy underline decoration-brand-gold decoration-2 underline-offset-4 transition-colors duration-300 hover:text-brand-gold motion-reduce:transition-none"
            >
              IBAS student achievement rates page
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
